/**
 * Pequeña variación del saludo para que los mensajes de una misma tanda no
 * sean idénticos. Solo toca la primera línea cuando es un saludo estándar
 * ("¡Hola Ana! 👋"); el resto del texto queda como está.
 */
const GREETINGS = [
  (n: string) => `¡Hola${n}! 👋`,
  (n: string) => `Hola${n} 😊`,
  (n: string) => `¡Hola${n}!`,
  (n: string) => `Hey${n} 🙌`,
  (n: string) => `¡Qué tal${n}! 👋`,
];

export function varyGreeting(text: string, seed: number): string {
  const m = text.match(/^¡?(?:Hola|Hey|Qué tal)(?:\s+([^!\n😊🙌👋]+?))?\s*!?\s*(?:[👋😊🙌])?[ \t]*(?:\r?\n|$)/u);
  if (!m) return text;
  const name = m[1] ? ` ${m[1].trim()}` : "";
  const pick = GREETINGS[Math.abs(seed) % GREETINGS.length];
  return pick(name) + "\n" + text.slice(m[0].length);
}

export function seedFrom(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return h;
}
// ---------------------------------------------------------------------------
// Nombre y tema para el saludo
// ---------------------------------------------------------------------------

const TITLES = new Set(["dr", "dra", "doctor", "doctora", "lic", "licda", "ing", "psic", "mtro", "mtra", "prof", "profe", "sr", "sra"]);

const NOT_A_NAME = new Set(
  (
    "coach coaching coaches vida vidas life mentor mentora terapeuta terapia psicologa psicologo " +
    "de del la el los las y e en para con por soy yo mi tu te tus mis un una ayudo " +
    "colombia mexico espana argentina chile peru venezuela ecuador bolivia uruguay paraguay panama " +
    "costa rica cuba usa latam latino latina world mundo " +
    "fit fitness health wellness salud bienestar official oficial tv news noticias radio shop store tienda " +
    "studio estudio club team equipo group grupo page blog cocina recetas culinary gastronomy gastronomic gastronomie gastronomia " +
    "mom mama papa mujer mujeres hombre hombres family familia " +
    "high performance system sistema digital marketing negocios business empresa empresarial " +
    "profesional professional holistica holistic conferencista presidente ceo " +
    "alma mente cuerpo espiritu amor luz light eternal embodiment"
  ).split(" "),
);

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const SEGMENT_SPLIT = /\s*(?:[|•·,/~()]|\s[-–—]\s)\s*/u;

function cleanTokens(segment: string): string[] {
  const cleaned = segment
    .replace(/[^\p{L}\s'’.]/gu, " ")
    .replace(/\./g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return [];
  const tokens = cleaned.split(" ").filter((t) => t.length > 0);
  while (tokens.length > 0 && TITLES.has(norm(tokens[0]))) tokens.shift();
  return tokens;
}

const looksLikeName = (t: string) => /^[\p{L}][\p{L}'’]{1,13}$/u.test(t) && !NOT_A_NAME.has(norm(t));

const titleCase = (t: string) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();

/**
 * Intenta sacar un nombre de pila real del nombre público de un perfil.
 * Devuelve "" cuando no hay un nombre claro ("Coach de vida", "Colombia"...),
 * para saludar solo con "¡Hola!" en vez de algo torpe como "Hola Coach".
 */
export function friendlyFirstName(displayName: string | null | undefined): string {
  const raw = (displayName ?? "").trim();
  if (!raw || /^https?:|\//.test(raw)) return "";
  const segments = raw.split(SEGMENT_SPLIT).filter((s) => s.trim().length > 0);

  // 1) Un nombre completo (2 o 3 palabras) en cualquier segmento.
  for (const seg of segments) {
    const tokens = cleanTokens(seg);
    if (tokens.length >= 2 && tokens.length <= 3 && tokens.every(looksLikeName)) return titleCase(tokens[0]);
  }

  // 2) Un solo nombre al inicio, si va acompañado de algo más (otro segmento o un emoji) y no está todo en mayúsculas.
  const first = segments[0] ?? "";
  const tokens = cleanTokens(first);
  const hasCompany = segments.length > 1 || /[^\p{L}\p{N}\s'’.]/u.test(first);
  if (tokens.length === 1 && hasCompany && looksLikeName(tokens[0]) && tokens[0].length <= 9 && tokens[0] !== tokens[0].toUpperCase()) {
    return titleCase(tokens[0]);
  }
  return "";
}

const TOPICS: Record<string, string> = {
  entrepreneur: " de emprendimiento",
  coach: " de coaching",
  "personal coach": " de coaching",
  therapist: " de bienestar",
  education: " de educación",
  "sports & fitness instruction": " de fitness",
  "health & wellness website": " de salud y bienestar",
  artist: " de arte",
  chef: " de cocina",
  "musician/band": " de música",
};

/** Frase de tema para el mensaje (" de coaching") o "" si el nicho no se puede decir bien en español. */
export function topicPhrase(niche: string | null | undefined): string {
  const key = (niche ?? "").trim().toLowerCase();
  if (!key || key === "sin definir" || key === "none") return "";
  return TOPICS[key] ?? "";
}

/** Mensaje inicial por defecto: saludo, algo concreto de su contenido y una pregunta abierta (sin promesas de ingresos). */
export function buildDefaultOpener(p: { name: string; niche?: string | null; platform: string }): string {
  const first = friendlyFirstName(p.name);
  const topic = topicPhrase(p.niche);
  return `¡Hola${first ? ` ${first}` : ""}! 👋\n\nVi tu contenido${topic} y me encantó cómo conectas con tu comunidad. Por eso pensé en ti: en VÍVELO AI estamos ayudando a creadores y emprendedores a organizar su prospección y su seguimiento con un sistema sencillo.\n\n¿Te gustaría que te cuente cómo lo están usando otros perfiles con una audiencia parecida a la tuya (${p.platform})?`;
}